// De kleine iconen van de beheervlakken (W6 ronde 2; ronde 3 voegde die van
// Bronnen & dekking toe). Owner-ontwerptaal: iconen boven tekstknoppen — maar
// hier altijd NAAST een woord, of met het woord in aria-label én tooltip, want
// de doelgroep leest geen pictogrammen zonder uitleg. Inline SVG op
// `currentColor`, zodat het icoon de kleur van zijn knop volgt.

const basis = {
  width: 14,
  height: 14,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2.2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
  style: { flexShrink: 0 },
};

/** Afvinken: "gezien en beoordeeld". */
export function VinkIcoon() {
  return (
    <svg {...basis}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/** Opnieuw meten: de wachters draaien. */
export function VerversIcoon() {
  return (
    <svg {...basis}>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <polyline points="21 3 21 9 15 9" />
    </svg>
  );
}

/** Kosten: een dollarteken in een rondje — de bedragen komen in USD binnen. */
export function DollarIcoon() {
  return (
    <svg {...basis} width={13} height={13} strokeWidth={2}>
      <circle cx="12" cy="12" r="10" />
      <path d="M15 8.5c-.6-.9-1.7-1.5-3-1.5-1.9 0-3 1-3 2.3 0 3.2 6 1.8 6 5 0 1.3-1.2 2.4-3 2.4-1.4 0-2.6-.6-3.2-1.6" />
      <line x1="12" y1="5" x2="12" y2="7" />
      <line x1="12" y1="17" x2="12" y2="19" />
    </svg>
  );
}

/** Stemmen: een duim omhoog — "ik wil deze bron erbij", zonder de weging te veranderen. */
export function StemIcoon() {
  return (
    <svg {...basis} strokeWidth={2}>
      <path d="M7 11v9H4v-9z" />
      <path d="M7 11l4-7a2 2 0 0 1 3 2l-1 4h6a2 2 0 0 1 2 2.3l-1.2 6A2 2 0 0 1 17.8 20H7" />
    </svg>
  );
}

/** Naar hoog: een pijl omhoog. */
export function HoogIcoon() {
  return (
    <svg {...basis}>
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

/** Naar normaal: een isgelijkteken — de gewone weging. */
export function NormaalIcoon() {
  return (
    <svg {...basis}>
      <line x1="5" y1="9" x2="19" y2="9" />
      <line x1="5" y1="15" x2="19" y2="15" />
    </svg>
  );
}

/** Blokkeren: een rondje met een streep. */
export function BlokIcoon() {
  return (
    <svg {...basis} strokeWidth={2}>
      <circle cx="12" cy="12" r="9" />
      <line x1="5.6" y1="5.6" x2="18.4" y2="18.4" />
    </svg>
  );
}

/** Opruimen: een archiefdoos — inplannen, niet wissen (V4). Bewust geen
 *  prullenbak: die belooft iets wat deze knop niet doet. */
export function OpruimIcoon() {
  return (
    <svg {...basis} strokeWidth={2}>
      <rect x="3" y="4" width="18" height="5" rx="1" />
      <path d="M5 9v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9" />
      <line x1="10" y1="13" x2="14" y2="13" />
    </svg>
  );
}

/** Toevoegen: een plus. */
export function PlusIcoon() {
  return (
    <svg {...basis}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

/** Nog niet gemeten: een klokje — de meter komt, alleen nog niet (W6 ronde 5). */
export function KlokIcoon() {
  return (
    <svg {...basis} strokeWidth={2}>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15.5 14" />
    </svg>
  );
}

/** De duim onder een chatantwoord; omlaag is dezelfde duim, gedraaid. */
export function DuimIcoon({ omlaag = false }: { omlaag?: boolean }) {
  return (
    <svg
      {...basis}
      strokeWidth={2}
      style={{ ...basis.style, transform: omlaag ? "rotate(180deg)" : undefined }}
    >
      <path d="M7 11v9H4v-9z" />
      <path d="M7 11l4-7a2 2 0 0 1 3 2l-1 4h6a2 2 0 0 1 2 2.3l-1.2 6A2 2 0 0 1 17.8 20H7" />
    </svg>
  );
}

/** Mislukt of fout: een driehoek met een uitroepteken. */
export function FoutIcoon() {
  return (
    <svg {...basis} strokeWidth={2}>
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

/** Twijfel: een vraagteken in een rondje. */
export function TwijfelIcoon() {
  return (
    <svg {...basis} strokeWidth={2}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6V14" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

/** Vertrouwelijk: een slotje — de inhoud van gesprekken is alleen voor beheerders (V2). */
export function SlotIcoon() {
  return (
    <svg {...basis} strokeWidth={2}>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

/** Sluiten: een kruis. */
export function KruisIcoon() {
  return (
    <svg {...basis}>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

/** Terug naar de standaard: een pijl die terugdraait (W6 ronde 6, Configuratie). */
export function TerugIcoon() {
  return (
    <svg {...basis}>
      <polyline points="9 14 4 9 9 4" />
      <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
    </svg>
  );
}

/** Voorstellen uit de betekenis: drie bolletjes die samen een groep vormen. */
export function GroepIcoon() {
  return (
    <svg {...basis} strokeWidth={2}>
      <circle cx="7" cy="8" r="3" />
      <circle cx="17" cy="8" r="3" />
      <circle cx="12" cy="17" r="3" />
    </svg>
  );
}

/** Op de wachtrij zetten: een postbak. */
export function WachtrijIcoon() {
  return (
    <svg {...basis} strokeWidth={2}>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}
